import React from 'react';
import { Paper, Box, Typography, List, ListItem, ListItemIcon, ListItemText, Button, LinearProgress, IconButton } from '@mui/material';
import { CheckCircle, RadioButtonUnchecked, Close, ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { colors } from '@/theme/tokens';

/**
 * steps: [{ key, label, description, done, href, cta }]
 * Returns null once every step is done, regardless of dismissed state.
 */
export default function OnboardingChecklist({ steps, onDismiss }) {
  const navigate = useNavigate();
  const completed = steps.filter(s => s.done).length;
  const pct = Math.round((completed / steps.length) * 100);
  if (completed === steps.length) return null;

  const nextStep = steps.find(s => !s.done);

  return (
    <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'primary.light', bgcolor: colors.primaryLight, borderRadius: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Finish setting up your school</Typography>
          <Typography variant="body2" color="text.secondary">{completed} of {steps.length} steps complete</Typography>
        </Box>
        <IconButton size="small" onClick={onDismiss} aria-label="Dismiss setup checklist"><Close fontSize="small" /></IconButton>
      </Box>

      <LinearProgress variant="determinate" value={pct} sx={{ my: 2, height: 8, borderRadius: 4 }} />

      <List dense>
        {steps.map(step => (
          <ListItem
            key={step.key}
            secondaryAction={!step.done && (
              <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate(step.href)}>{step.cta || 'Set up'}</Button>
            )}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {step.done ? <CheckCircle sx={{ color: colors.success }} /> : <RadioButtonUnchecked sx={{ color: 'text.disabled' }} />}
            </ListItemIcon>
            <ListItemText
              primary={step.label}
              secondary={step.description}
              sx={{ textDecoration: step.done ? 'line-through' : 'none', opacity: step.done ? 0.6 : 1 }}
            />
          </ListItem>
        ))}
      </List>

      {nextStep && (
        <Button variant="contained" onClick={() => navigate(nextStep.href)} sx={{ mt: 1 }}>
          Continue: {nextStep.label}
        </Button>
      )}
    </Paper>
  );
}
